import type { RestaurantTable as GeneratedTable } from '@agos/api-client/model'

/**
 * The floor plan's own shapes.
 *
 * `GET /restaurant-tables` is typed now (BLOCKED #4), so the fourteen fields this used to declare
 * come from the generated model — including `zone` and `color` as `string | null`, which is FB-051's
 * finding on the response side.
 *
 * **`shape` stays narrowed here.** The backend answers a `String`, deliberately: a shape added
 * tomorrow should reach a screen that can say it does not know it, rather than a deserialiser that
 * rejects the whole floor plan. The canvas draws two and wants the union, so the narrowing lives
 * where the drawing does.
 */
export type RestaurantTable = Omit<GeneratedTable, 'shape'> & { shape: 'rect' | 'circle' }

export function tablesFrom(payload: unknown): RestaurantTable[] {
  const tables = (payload as { tables?: unknown } | undefined)?.tables
  return Array.isArray(tables) ? (tables as RestaurantTable[]) : []
}

/**
 * The fields a table write may carry.
 *
 * `zone` and `color` are genuinely nullable — sending `null` clears them, and the backend does
 * exactly that. Springdoc used to emit both as plain `string?`, so the generated request types could
 * not express *clear this* and an adapter cast around the gap; the DTOs carry `@Schema(nullable =
 * true)` now and the generated types say `string | null`, so the cast is gone and the difference is
 * expressed where it belongs — in the type.
 *
 * Sending `undefined` is still a different thing: it drops the key and leaves the stored value alone.
 *
 * **`zoneId` joined this list in §9.337** and follows the same rule: `null` clears the zone,
 * `undefined` leaves it. It is the field a register hangs off (`BACKEND.md` §10A.125), which is why
 * the free-text `zone` beside it is no longer the only way to group a table.
 */
export type TableWriteFields = Partial<
  Pick<
    RestaurantTable,
    'name' | 'capacity' | 'x' | 'y' | 'width' | 'height' | 'shape' | 'zone' | 'zoneId' | 'color'
  >
>

